// src/components/OrderDetailModal.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE =
  (process.env.REACT_APP_API_URL &&
    process.env.REACT_APP_API_URL.replace(/\/+$/, '')) ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : window.location.origin);


function OrderDetailModal({ order: initialOrder, onClose }) {
  const { user, token } = useAuth();
  console.log('OrderDetailModal: Usuario obtenido del contexto:', user);

  const [order, setOrder] = useState(initialOrder);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [currentTab, setCurrentTab] = useState('resumen');

  // --- Estados y lógica para la pestaña de FACTURACION ---
  const [invoiceData, setInvoiceData] = useState({
    order_id: initialOrder.id,
    branch: 'eCommerce',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    client_name: initialOrder.cliente_nombre || '',
    client_rfc: initialOrder.raw_data?.CustomerDocument?.DocNumber || 'XAXX010101000',
  });
  const [invoiceMessage, setInvoiceMessage] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const [pdfFile, setPdfFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [invoiceNumberInput, setInvoiceNumberInput] = useState('');

  const [desgloseItems, setDesgloseItems] = useState([]);
  const [associatedInvoices, setAssociatedInvoices] = useState([]);

  // --- Estados y lógica para la nueva pestaña de PAQUETERIA ---
  const [newGuideNumberInput, setNewGuideNumberInput] = useState('');
  const [newGuidePdfFile, setNewGuidePdfFile] = useState(null);
  const [newGuideCarrier, setNewGuideCarrier] = useState(''); // Nuevo estado para la paquetería
  const [newGuideMessage, setNewGuideMessage] = useState('');
  const [newGuideLoading, setNewGuideLoading] = useState(false);

  // Opciones de paquetería
  const carrierOptions = [
    { value: '', label: 'Selecciona Paquetería' },
    { value: 'PaqExp', label: 'Paquetexpress' },
    { value: 'FedEx', label: 'FedEx' },
    { value: 'Sendex', label: 'Sendex' },
    { value: 'Estafeta', label: 'Estafeta' },
    { value: 'UPS', label: 'UPS' },
    { value: 'DHL', label: 'DHL' },
    { value: 'Afimex', label: 'Afimex' },
    { value: 'Coppel', label: 'Coppel' },
    { value: 'Amazon', label: 'Amazon' },
    { value: 'ML', label: 'Mercado Libre' }, // Aunque ML viene de AutoAzur, se puede listar aquí
  ];


  // Efecto para cargar las facturas asociadas a este pedido
  useEffect(() => {
    const fetchAssociatedInvoices = async () => {
      if (!order?.id) {
        console.warn('fetchAssociatedInvoices: No hay order.id, omitiendo fetch.');
        return;
      }
      if (!token) {
        console.warn('fetchAssociatedInvoices: No hay token, omitiendo fetch.');
        return;
      }
      console.log('Token siendo enviado (fetchAssociatedInvoices):', token);
      try {
        // CORRECCIÓN: Usar ruta relativa para la API y pasar orderId como query param
        const response = await fetch(`/api/invoices?orderId=${order.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setAssociatedInvoices(data.invoices);
        } else {
          console.error('Error al cargar facturas asociadas:', data.mensaje);
        }
      } catch (err) {
        console.error('Error de red al cargar facturas asociadas:', err);
      }
    };
    fetchAssociatedInvoices();
  }, [order, token]);


  // Helper para limpiar prefijos duplicados del SKU (igual que en picking.js)
  const cleanSkuPrefixes = (sku) => {
    if (!sku) return '';
    let cleaned = sku.toUpperCase();
    while (cleaned.startsWith('2PZ-')) {
      cleaned = cleaned.substring(4);
    }
    while (cleaned.startsWith('MSI-')) {
      cleaned = cleaned.substring(4);
    }
    return cleaned;
  };

  // Efecto para desglosar los ítems del pedido para la visualización de la factura
  useEffect(() => {
    if (order && order.raw_data?.Items) {
      const items = order.raw_data.Items.map(item => {
        const originalSkuOrder = item.Sku;
        const itemTitleOrder = item.Titulo;
        const quantityOrderedPackage = item.Quantity;
        const unitPricePackage = parseFloat(item.UnitPrice || item.OriginalPrice || item.Total || 0);

        let unitQuantityInPackage = 1;
        let singleUnitSku = originalSkuOrder; // Por defecto, el SKU original
        let displaySku = originalSkuOrder; // SKU para mostrar, por defecto el original

        // Aplicar la misma lógica de limpieza de prefijos aquí para la visualización
        // y construir el display_sku correctamente
        let tempSkuForCleaning = originalSkuOrder.toUpperCase();
        let is2PZDetected = false;
        let isMSIDetected = false;

        if (tempSkuForCleaning.startsWith('2PZ-')) {
            is2PZDetected = true;
            unitQuantityInPackage = 2;
            while (tempSkuForCleaning.startsWith('2PZ-')) {
                tempSkuForCleaning = tempSkuForCleaning.substring(4);
            }
        } else if (tempSkuForCleaning.startsWith('MSI-')) {
            isMSIDetected = true;
            unitQuantityInPackage = 1; // MSI generalmente no altera la cantidad de unidades físicas
            while (tempSkuForCleaning.startsWith('MSI-')) {
                tempSkuForCleaning = tempSkuForCleaning.substring(4);
            }
        } else if (itemTitleOrder.toUpperCase().includes('2 LLANTAS') || itemTitleOrder.toUpperCase().includes('2PZ')) {
            unitQuantityInPackage = 2;
        }

        // Construir el displaySku basado en la detección y el SKU limpio
        if (is2PZDetected) {
            displaySku = `2PZ-${tempSkuForCleaning}`;
        } else if (isMSIDetected) {
            displaySku = `MSI-${tempSkuForCleaning}`;
        } else {
            displaySku = originalSkuOrder; // Si no hay prefijos especiales, usar el original
        }
        
        singleUnitSku = tempSkuForCleaning; // El SKU base limpio para cálculos internos


        const singleUnitPrice = unitPricePackage / unitQuantityInPackage;
        const totalSingleUnits = quantityOrderedPackage * unitQuantityInPackage;
        
        const itemSubtotal = singleUnitPrice * totalSingleUnits;
        const itemTaxIva = itemSubtotal * 0.16;
        const itemTotal = itemSubtotal + itemTaxIva;

        return {
          product_id: null,
          original_sku_order: originalSkuOrder, // Mantener el original para referencia
          display_sku: displaySku, // SKU para mostrar en la UI
          item_title_order: itemTitleOrder,
          quantity_ordered_package: quantityOrderedPackage,
          unit_price_package: unitPricePackage,
          unit_quantity_in_package: unitQuantityInPackage,
          single_unit_sku: singleUnitSku,
          single_unit_price: singleUnitPrice,
          total_single_units: totalSingleUnits,
          subtotal_item: itemSubtotal,
          tax_iva_item: itemTaxIva,
          total_item: itemTotal
        };
      });
      setDesgloseItems(items);
    }
  }, [order]);


  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'pdfFile') {
      setPdfFile(files[0]);
    } else if (name === 'xmlFile') {
      setXmlFile(files[0]);
    }
  };

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    setInvoiceMessage('');
    setInvoiceLoading(true);

    if (!invoiceData.client_name || !invoiceData.invoice_date || !invoiceData.branch) {
      setInvoiceMessage('Error: Nombre del cliente, fecha de factura y sucursal son obligatorios.');
      setInvoiceLoading(false);
      return;
    }

    if ((pdfFile || xmlFile) && !invoiceNumberInput.trim()) {
        setInvoiceMessage('Error: Si adjuntas archivos, el número de factura es obligatorio.');
        setInvoiceLoading(false);
        return;
    }
    if (!token) {
      setInvoiceMessage('Error: No hay token de autenticación. Por favor, inicia sesión de nuevo.');
      setInvoiceLoading(false);
      return;
    }

    try {
      // Si el número de factura manual está vacío, generamos uno temporal para la creación
      const tempInvoiceNumber = invoiceNumberInput.trim() || `INV-${order.id}-${Date.now()}`;
      console.log('DEBUG: tempInvoiceNumber para POST /api/invoices:', tempInvoiceNumber); // DEBUG LOG

      const invoicePayload = {
        invoice_number: tempInvoiceNumber, // Usamos el temporal o el manual
        order_id: invoiceData.order_id,
        branch: invoiceData.branch,
        invoice_date: invoiceData.invoice_date,
        due_date: invoiceData.due_date || null,
        client_name: invoiceData.client_name,
        client_rfc: invoiceData.client_rfc || null,
        subtotal: order.raw_data?.SubTotal || 0, // Tomar del raw_data
        tax_iva: order.raw_data?.IVA || 0, // Tomar del raw_data
        total: order.raw_data?.Total || 0, // Tomar del raw_data
      };

      console.log('Token siendo enviado (generateInvoice):', token);
      console.log('DEBUG: Payload enviado a POST /api/invoices:', invoicePayload); // DEBUG LOG
  const generateInvoiceResponse = await fetch(`${API_BASE}/api/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(invoicePayload),
      });

      const generateInvoiceData = await generateInvoiceResponse.json();
      console.log('DEBUG: Respuesta de POST /api/invoices:', generateInvoiceData); // DEBUG LOG


      if (!generateInvoiceResponse.ok) {
        setInvoiceMessage(`Error al generar factura: ${generateInvoiceData.mensaje || 'Error desconocido'}`);
        setInvoiceLoading(false);
        return;
      }

      const newInvoiceId = generateInvoiceData.id; // El backend devuelve 'id'
      const generatedInvoiceNumber = generateInvoiceData.invoice_number; // El backend devuelve 'invoice_number'
      setInvoiceMessage(`Factura ${generatedInvoiceNumber} generada exitosamente.`);
      console.log('DEBUG: generatedInvoiceNumber extraído de la respuesta:', generatedInvoiceNumber); // DEBUG LOG


      if (pdfFile || xmlFile) {
        const formData = new FormData();
        if (pdfFile) formData.append('pdfFile', pdfFile);
        if (xmlFile) formData.append('xmlFile', xmlFile);
        
        console.log('Token siendo enviado (uploadFiles):', token);
        const uploadResponse = await fetch(`${API_BASE}/api/invoices/${newInvoiceId}/upload-files`, {
          method: 'POST', // Cambiado a POST según tu backend
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });

        const uploadData = await uploadResponse.json();
        if (!uploadResponse.ok) {
          setInvoiceMessage(`Factura generada, pero error al subir archivos: ${uploadData.mensaje || 'Error desconocido'}`);
          setInvoiceLoading(false);
          return;
        }
        setInvoiceMessage(uploadData.mensaje);
      }

      console.log('Token siendo enviado (updateOrderFactura):', token);
      console.log('DEBUG: Enviando numero_factura al pedido (PUT /api/orders/:id):', generatedInvoiceNumber); // DEBUG LOG
      const updatedOrderResponse = await fetch(`${API_BASE}/api/orders/${order.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ numero_factura: generatedInvoiceNumber })
      });
      if (updatedOrderResponse.ok) {
        // CORRECCIÓN: Usar ruta relativa para la API
        const refreshedOrder = await fetch(`${API_BASE}/api/orders/${order.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const refreshedOrderData = await refreshedOrder.json();
        if (refreshedOrder.ok) {
          setOrder(refreshedOrderData);
          console.log('DEBUG: Pedido refrescado en el modal con datos actualizados:', refreshedOrderData); // DEBUG LOG
        }
      }

      // Refrescar la lista de facturas asociadas en el modal
      const refreshAssociatedInvoices = async () => {
        if (!order?.id) return;
        console.log('Token siendo enviado (refreshAssociatedInvoices):', token);
        try {
          // CORRECCIÓN: Usar ruta relativa para la API
          const response = await fetch(`${API_BASE}/api/invoices?orderId=${order.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          if (response.ok) {
            setAssociatedInvoices(data.invoices);
            console.log('DEBUG: Facturas asociadas refrescadas:', data.invoices); // DEBUG LOG
          }
        } catch (err) {
          console.error('Error al refrescar facturas asociadas:', err);
        }
      };
      refreshAssociatedInvoices();

    } catch (err) {
      console.error('Error de red al generar/subir factura:', err);
      setInvoiceMessage('Error de conexión con el servidor.');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    if (!token) {
      alert('No hay token de autenticación para descargar el PDF.');
      return;
    }
    console.log('Token siendo enviado (downloadPdf):', token);
    try {
      // CORRECCIÓN: Usar ruta relativa para la API
      const response = await fetch(`${API_BASE}/api/invoices/${invoiceId}/download?type=pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const errorData = await response.json();
        alert(`Error al descargar PDF: ${errorData.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('Error de red al descargar PDF:', err);
      alert('Error de conexión al descargar PDF.');
    }
  };

  const handleDownloadXml = async (invoiceId, invoiceNumber) => {
    if (!token) {
      alert('No hay token de autenticación para descargar el XML.');
      return;
    }
    console.log('Token siendo enviado (downloadXml):', token);
    try {
      // CORRECCIÓN: Usar ruta relativa para la API
      const response = await fetch(`${API_BASE}/api/invoices/${invoiceId}/download?type=xml`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_${invoiceNumber}.xml`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const errorData = await response.json();
        alert(`Error al descargar XML: ${errorData.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('Error de red al descargar XML:', err);
      alert('Error de conexión al descargar XML.');
    }
  };

  // --- Lógica de la nueva pestaña de PAQUETERIA ---
  const handleNewGuideFileChange = (e) => {
    const { name, files } = e.target;
    if (name === 'newGuidePdfFile') {
      setNewGuidePdfFile(files[0]);
    }
  };

  const handleAttachNewGuide = async (e) => {
    e.preventDefault();
    setNewGuideMessage('');
    setNewGuideLoading(true);

    if (!newGuidePdfFile) {
      setNewGuideMessage('Error: Debes adjuntar un archivo PDF.');
      setNewGuideLoading(false);
      return;
    }
    if (!newGuideCarrier) { // Validación de paquetería
      setNewGuideMessage('Error: Debes seleccionar una paquetería.');
      setNewGuideLoading(false);
      return;
    }
    if (!token) {
      setNewGuideMessage('Error: No hay token de autenticación. Por favor, inicia sesión de nuevo.');
      setNewGuideLoading(false);
      return;
    }

    const formData = new FormData();
    if (newGuideNumberInput.trim()) {
      formData.append('guideNumber', newGuideNumberInput.trim());
    }
    formData.append('pdfFile', newGuidePdfFile);
    formData.append('carrierName', newGuideCarrier); // Añadir la paquetería seleccionada

    try {
      console.log('Token siendo enviado (attachNewGuide):', token);
      // CORRECCIÓN: Usar ruta relativa para la API
      const response = await fetch(`${API_BASE}/api/orders/${order.id}/guides/upload-attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setNewGuideMessage(data.mensaje);
        setNewGuideNumberInput('');
        setNewGuidePdfFile(null);
        setNewGuideCarrier(''); // Limpiar el campo de paquetería
        // Refrescar el pedido para mostrar las nuevas guías
        // CORRECCIÓN: Usar ruta relativa para la API
        const refreshedOrder = await fetch(`${API_BASE}/api/orders/${order.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const refreshedOrderData = await refreshedOrder.json();
        if (refreshedOrder.ok) {
          setOrder(refreshedOrderData);
        }
      } else {
        setNewGuideMessage(`Error: ${data.mensaje || 'Error al adjuntar guía.'}`);
      }
    } catch (err) {
      console.error('Error de red al adjuntar guía:', err);
      setNewGuideMessage('Error de conexión con el servidor.');
    } finally {
      setNewGuideLoading(false);
    }
  };

  const handleDownloadAttachedFile = (url, filename) => {
    if (!url) {
      alert('No hay archivo disponible para descargar.');
      return;
    }
    // Si la URL es relativa, la convertimos en absoluta usando el origen actual
    // Asumimos que los archivos están en /uploads/
    const fullUrl = url.startsWith('/') ? window.location.origin + url : url;
    window.open(fullUrl, '_blank');
  };


  // --- Lógica del modal de detalles de pedido (existente) ---
  const internalStatuses = [
    'Inventario Pendiente', 'Confirmada', 'Preparación', 'Listo para envío',
    'Enviada', 'Cancelada', 'Pendiente de factura'
  ];

  const getStatusColorClass = (status) => {
    switch (status) {
      case 'Confirmada': return 'bg-green-500';
      case 'Preparación': return 'bg-orange-500';
      case 'Listo para envío': return 'bg-blue-500';
      case 'Enviada': return 'bg-teal-500';
      case 'Cancelada': return 'bg-red-500';
      case 'Pendiente de factura': return 'bg-purple-500';
      case 'Inventario Pendiente': return 'bg-yellow-500'; // Color para el nuevo estado
      default: return 'bg-gray-400';
    }
  };

  const formatAddress = (shipment) => {
    if (!shipment) return 'N/A';
    const parts = [
      shipment.Street, shipment.Number, shipment.Neighborhood,
      shipment.City, shipment.State, shipment.Country,
      shipment.ZipCode ? `C.P. ${shipment.ZipCode}` : ''
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatMercadoLibreGuide = (guideNumber) => {
    if (!guideNumber) return 'N/A';
    const match = guideNumber.match(/MEL(\d+)(?:LMXDF01|FMDOF01)/);
    if (match && match[1]) { return match[1]; }
    return guideNumber;
  };

  // Helper para validar si un número de guía es "válido" para transiciones de estado
  const isValidGuideForTransition = (guideNumber) => {
    if (!guideNumber || typeof guideNumber !== 'string') return false;
    const cleanedGuide = guideNumber.trim().toUpperCase();
    return cleanedGuide !== '' &&
           cleanedGuide !== 'N/A' &&
           !/^GUIA-\d+-\d+$/.test(guideNumber) && // Nuestro placeholder
           !cleanedGuide.includes('PENDING') &&
           !cleanedGuide.includes('GENERATING');
  };

  // Recargar los detalles del pedido si initialOrder cambia (ej. al reabrir el modal)
  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!initialOrder || !initialOrder.id) return;
      setLoading(true);
      setError(null);
      if (!token) {
        setError('No hay token de autenticación. Por favor, inicia sesión de nuevo.');
        setLoading(false);
        return;
      }
      console.log('Token siendo enviado (fetchOrderDetail):', token);
      try {
        // CORRECCIÓN: Usar ruta relativa para la API
        const response = await fetch(`${API_BASE}/api/orders/${initialOrder.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setOrder(data);
          setInvoiceData(prev => ({
            ...prev,
            order_id: data.id,
            client_name: data.cliente_nombre || '',
            client_rfc: data.raw_data?.CustomerDocument?.DocNumber || 'XAXX010101000',
          }));
          setInvoiceNumberInput('');
          setPdfFile(null);
          setXmlFile(null);
          setInvoiceMessage('');
        } else {
          setError(data.mensaje || 'Error al cargar detalles del pedido.');
        }
      } catch (err) {
        console.error('OrderDetailModal: Error de red al obtener detalles del pedido:', err);
        setError('Error de conexión al cargar detalles del pedido.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetail();
  }, [initialOrder, token]);


  const handlePrintAndMarkGuide = async (guideToPrintId = null, guideNumberToPrint = null) => {
    if (!order || !order.id) return;

    const performedByUserId = user?.id || null;
    const performedByUsername = user?.nombre || 'Desconocido'; 

    setStatusMessage('Obteniendo guía y marcando como impresa...');
    if (!token) {
      setStatusMessage('Error: No hay token de autenticación. Por favor, inicia sesión de nuevo.');
      return;
    }

    try {
      let finalGuideNumber = guideNumberToPrint;
      let guideIdToUse = guideToPrintId;
      let isNewGuideGenerated = false;

      if (!guideToPrintId) {
        console.log('Token siendo enviado (generatePrintGuide):', token);
        // CORRECCIÓN: Usar ruta relativa para la API
        const generateResponse = await fetch(`${API_BASE}/api/orders/${order.id}/print-guide`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            performed_by_user_id: performedByUserId,
            performed_by_username: performedByUsername
          })
        });
        const generateData = await generateResponse.json();

        if (!generateResponse.ok) {
          setStatusMessage(`Error al generar nueva guía: ${generateData.mensaje || 'Error desconocido'}`);
          return;
        }
        setStatusMessage(generateData.mensaje);
        guideIdToUse = generateData.guide_id;
        finalGuideNumber = generateData.numero_guia_generada;
        isNewGuideGenerated = true;
      }

      if (!finalGuideNumber || finalGuideNumber === 'N/A') {
        setStatusMessage('Error: No hay un número de guía válido para imprimir.');
        return;
      }

      console.log('Token siendo enviado (getLabel):', token);
      // CORRECCIÓN: Usar ruta relativa para la API
      const response = await fetch(`${API_BASE}/api/orders/${order.id}/label?folios=${order.folio_autoazur}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al obtener el PDF de la guía: ${errorText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          // printWindow.print();
        };
      } else {
        setStatusMessage('El navegador bloqueó la ventana de impresión. Por favor, permite pop-ups para este sitio.');
      }

      if (!isNewGuideGenerated && guideIdToUse) {
           console.log('Token siendo enviado (markPrintGuide):', token);
           // CORRECCIÓN: Usar ruta relativa para la API
           const markPrintedResponse = await fetch(`${API_BASE}/api/orders/${order.id}/print-guide`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    guide_id: guideIdToUse,
                    performed_by_user_id: performedByUserId,
                    performed_by_username: performedByUsername
                })
            });
            const markPrintedData = await markPrintedResponse.json();
            if (!markPrintedResponse.ok) {
                console.error('Error al marcar guía como impresa después de imprimir:', markPrintedData.mensaje);
                setStatusMessage(`Guía impresa, pero error al marcar como impresa en DB: ${markPrintedData.mensaje}`);
            }
      }

      // ¡CORRECCIÓN CLAVE AQUÍ! Re-fetch del pedido para actualizar el estado local del modal
      console.log('Token siendo enviado (refrescar pedido en modal):', token);
      const refreshedOrderResponse = await fetch(`${API_BASE}/api/orders/${order.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const refreshedOrderData = await refreshedOrderResponse.json();
      if (refreshedOrderResponse.ok) { 
        setOrder(refreshedOrderData); // Actualiza el estado 'order' del modal con los datos más recientes
        setStatusMessage('Guía obtenida e impresa. Estado actualizado.');

        // NUEVA LÓGICA: Verificar si todas las guías están impresas y cambiar a Preparación
        const updatedOrderGuides = refreshedOrderData.guides || [];
        const allValidGuidesPrinted = updatedOrderGuides.length > 0 && 
                                      updatedOrderGuides.every(g => g.print_status === 1 && isValidGuideForTransition(g.guide_number));
        
        if (allValidGuidesPrinted && refreshedOrderData.estado_interno === 'Confirmada') {
            console.log('Todas las guías válidas impresas. Intentando cambiar estado a Preparación.');
            await handleStatusChange({ target: { value: 'Preparación' } }, refreshedOrderData.id); // Llama a la función de cambio de estado
        }

      } else {
        setStatusMessage(`Error al refrescar pedido en modal: ${refreshedOrderData.mensaje || 'Error desconocido'}`);
      }

    } catch (err) {
      console.error('OrderDetailModal: Error al imprimir guía:', err);
      setStatusMessage(`Error al imprimir guía: ${err.message}`);
    } finally {
        setLoading(false);
    }
  };


  const handleStatusChange = async (e, orderIdToUpdate = order.id) => { // Acepta orderIdToUpdate para llamadas externas
    const newStatus = e.target.value;
    const oldStatus = order.estado_interno; // Usa el estado 'order' actual del modal
    
    if (!order || !order.id || newStatus === oldStatus) {
      return;
    }

    const performedByUserId = user?.id || null;
    const performedByUsername = user?.nombre || 'Desconocido';

    setStatusMessage('Actualizando estatus...');
    if (!token) {
      setStatusMessage('Error: No hay token de autenticación. Por favor, inicia sesión de nuevo.');
      return;
    }

    // Validaciones de frontend antes de enviar al backend
    const associatedGuides = order.guides || []; // Usa las guías del estado 'order' actual
    const hasValidPrintedGuide = associatedGuides.some(guide => guide.print_status === 1 && isValidGuideForTransition(guide.guide_number));
    const hasAnyValidGuide = associatedGuides.some(guide => isValidGuideForTransition(guide.guide_number));

    let canProceed = true;
    let errorMessage = '';

    if (user?.rol !== 'admin') { // Solo aplica validaciones si no es admin
      switch (oldStatus) {
        case 'Inventario Pendiente': // 'Recibido' ya no existe
          if (newStatus !== 'Confirmada' && newStatus !== 'Pendiente de factura' && newStatus !== 'Cancelada') {
            canProceed = false; errorMessage = `Transición inválida de "${oldStatus}" a "${newStatus}".`;
          }
          break;
        case 'Confirmada':
          if (newStatus !== 'Preparación' && newStatus !== 'Cancelada') {
            canProceed = false; errorMessage = `Transición inválida de "${oldStatus}" a "${newStatus}".`;
          } else if (newStatus === 'Preparación' && !hasValidPrintedGuide) {
            canProceed = false; errorMessage = 'No se puede pasar a "Preparación" sin guía válida e impresa.';
          }
          break;
        case 'Preparación':
          if (newStatus !== 'Listo para envío' && newStatus !== 'Cancelada') {
            canProceed = false; errorMessage = `Transición inválida de "${oldStatus}" a "${newStatus}".`;
          }
          break;
        case 'Listo para envío':
          if (newStatus !== 'Enviada' && newStatus !== 'Cancelada') {
            canProceed = false; errorMessage = `Transición inválida de "${oldStatus}" a "${newStatus}".`;
          } else if (newStatus === 'Enviada' && !hasAnyValidGuide) {
            canProceed = false; errorMessage = 'No se puede pasar a "Enviada" sin guía válida.';
          }
          break;
        case 'Enviada':
        case 'Cancelada':
        case 'Pendiente de factura':
          canProceed = false; errorMessage = `El pedido ya está en estado "${oldStatus}" y no puede ser modificado.`;
          break;
        default:
          canProceed = false; errorMessage = `Transición de estado inválida.`;
          break;
      }
    }

    if (!canProceed) {
      setStatusMessage(`Error: ${errorMessage}`);
      return;
    }


    try {
      console.log('Token siendo enviado (updateStatus):', token);
      // CORRECCIÓN: Usar ruta relativa para la API
      const response = await fetch(`${API_BASE}/api/orders/${orderIdToUpdate}/status`, { // Usa orderIdToUpdate
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          estado_interno: newStatus,
          performed_by_user_id: performedByUserId,
          performed_by_username: performedByUsername
        })
      });

      const data = await response.json();

      if (response.ok) {
        setOrder(data.pedido); // Actualiza el estado 'order' del modal
        setStatusMessage(`Estatus actualizado a "${newStatus}" exitosamente.`);
      } else {
        setStatusMessage(`Error al actualizar estatus: ${data.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('OrderDetailModal: Error de red al actualizar estatus:', err);
      setStatusMessage('Error de conexión al intentar actualizar estatus.');
    }
  };


  if (!order || loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity50 flex items-center justify-center z-50 p-4">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full text-center">
          {loading ? 'Cargando detalles del pedido...' : 'No hay pedido seleccionado.'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full text-center text-red-600">
              Error: {error}
              <button onClick={onClose} className="mt-4 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg">Cerrar</button>
            </div>
          </div>
        );
      }

      const rawOrderData = order.raw_data;
      const shipmentData = rawOrderData?.Shipment;
      const itemsData = rawOrderData?.Items || [];
      const orderHistory = order.history || [];
      const orderGuides = order.guides || [];
      
      const subtotalItems = itemsData.reduce((acc, item) => acc + (parseFloat(item.UnitPrice || 0) * item.Quantity), 0);
      const shippingCost = parseFloat(rawOrderData.ShippingCost || 0);
      const taxes = parseFloat(rawOrderData.IVA || 0);
      // CORRECCIÓN: Tomar grandTotal directamente del raw_data de AutoAzur
      const grandTotal = parseFloat(rawOrderData.Total || 0); 

      return (
        // Overlay del modal: cierra al hacer clic fuera
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            {/* Botón de cerrar - Ahora fuera del contenido principal */}
            <button
              onClick={onClose}
              className="absolute top-2 right-2 md:top-4 md:right-4 bg-gray-800 text-white rounded-full h-8 w-8 flex items-center justify-center text-xl font-bold hover:bg-gray-700 transition duration-200 z-50"
              title="Cerrar"
            >
              &times;
            </button>
          {/* Contenido del modal: evita que se cierre al hacer clic dentro */}
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
            

            {/* Header del Modal */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Detalles del Pedido</h2>
              <div className="flex items-center space-x-2">
                <span className="text-xl font-semibold text-gray-700"># Pedido: {order.referencia_plataforma}</span>
                {/* Selector de Estatus */}
                <select
                  value={order.estado_interno}
                  onChange={handleStatusChange}
                  disabled={user?.rol !== 'admin'}
                  className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColorClass(order.estado_interno)} text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${user?.rol !== 'admin' ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {internalStatuses.map(status => (
                    <option key={status} value={status} className="bg-gray-700 text-white">
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mini-menú de pestañas */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                className={`py-2 px-4 text-sm font-medium ${currentTab === 'resumen' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                onClick={() => setCurrentTab('resumen')}
              >
                RESUMEN
              </button>
              <button
                className={`py-2 px-4 text-sm font-medium ${currentTab === 'facturacion' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                onClick={() => setCurrentTab('facturacion')}
              >
                FACTURACION
              </button>
              <button
                className={`py-2 px-4 text-sm font-medium ${currentTab === 'paqueteria' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
                onClick={() => setCurrentTab('paqueteria')}
              >
                PAQUETERIA
              </button>
            </div>

            {/* Contenido de la pestaña actual */}
            {currentTab === 'resumen' && (
              <>
                {/* Mensaje de estado de actualización */}
                {statusMessage && (
                  <div className={`p-2 mb-4 text-center rounded-lg ${statusMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {statusMessage}
                  </div>
                )}

                {/* Contenido del Modal en Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Detalles de Venta (Producto) */}
                  <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Detalles de Venta</h3>
                    <div className="space-y-2">
                      {desgloseItems.map((item, index) => ( // Usar desgloseItems para la visualización
                        <div key={index} className="flex items-center border-b pb-2 last:border-b-0">
                          <img
                            src={`https://placehold.co/60x60/cccccc/333333?text=SKU`}
                            alt={item.item_title_order}
                            className="w-16 h-16 object-cover rounded-lg mr-4"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{item.item_title_order}</p>
                            {/* Mostrar el SKU limpio para el usuario */}
                            <p className="text-sm text-gray-600">
                              SKU: {item.display_sku} 
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-700">Cantidad: {item.quantity_ordered_package}</p>
                            <p className="font-bold text-gray-900">${parseFloat(item.unit_price_package || 0).toFixed(2)} / U</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-right mt-4 pt-4 border-t space-y-1">
                        <p className="text-md text-gray-700">Subtotal productos: <span className="font-semibold">${subtotalItems.toFixed(2)}</span></p>
                        <p className="text-md text-gray-700">Envío: <span className="font-semibold">${shippingCost.toFixed(2)}</span></p>
                        <p className="text-md text-gray-700">Impuestos (IVA): <span className="font-semibold">${taxes.toFixed(2)}</span></p>
                        <p className="text-xl font-bold text-gray-900 mt-2">Total Pedido: <span className="text-blue-600">${grandTotal.toFixed(2)}</span></p>
                    </div>
                  </div>

                  {/* Forma de Pago */}
                  <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Forma de Pago</h3>
                    <p className="text-gray-700"><strong>Plataforma:</strong> {order.canal_origen}</p>
                    <p className="text-gray-700"><strong>Método de Pago:</strong> {rawOrderData?.PaymentMethod || 'N/A'}</p>
                    <p className="text-gray-700"><strong>Empresa:</strong> {rawOrderData?.AccountName || 'N/A'}</p>
                    <p className="text-gray-700"><strong>Factura:</strong> {order.numero_factura || 'N/A'}</p>
                    <p className="text-gray-700"><strong>Referencia:</strong> {order.referencia_plataforma}</p>
                  </div>

                  {/* Info de Envío */}
                  <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Info de Envío</h3>
                    <p className="text-gray-700"><strong>Paquetería:</strong> {order.metodo_envio || 'N/A'}</p>
                    <p className="text-gray-700"><strong>Dirección:</strong> {formatAddress(shipmentData)}</p>
                    <p className="text-gray-700"><strong>Cliente:</strong> {order.cliente_nombre}</p>
                    <p className="text-gray-700"><strong>Teléfono:</strong> {order.cliente_telefono || 'N/A'}</p>
                    <p className="text-gray-700"><strong>Email:</strong> {order.cliente_email || 'N/A'}</p>
                    
                    {/* Sección de Guías Múltiples */}
                    <div className="mt-3">
                      <p className="font-semibold text-gray-800 mb-2">Guía(s):</p>
                      {orderGuides.length > 0 ? (
                        orderGuides.map((guide, index) => (
                          <div key={guide.id || index} className="flex items-center justify-between text-sm text-gray-700 mb-1">
                            <span>
                              {order.canal_origen === 'MERCADO LIBRE' ? formatMercadoLibreGuide(guide.guide_number) : guide.guide_number || 'N/A'}
                              {guide.print_status === 1 ? (
                                <span className="ml-2 text-green-600 font-medium">Impresa &#10003;</span>
                              ) : (
                                <span className="ml-2 text-red-600 font-medium">No Impresa &#10007;</span>
                              )}
                            </span>
                            <div className="flex space-x-2">
                              {/* Botón de Imprimir Guía (ahora también marca como impresa) */}
                              <button
                                onClick={() => handlePrintAndMarkGuide(guide.id, guide.guide_number)}
                                className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-1 px-3 rounded-lg text-xs transition duration-200"
                                title="Imprimir esta guía y marcarla como impresa"
                              >
                                Imprimir Guía
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-600">No hay guías asociadas.</p>
                      )}
                      {/* Botón para generar una nueva guía y marcarla como impresa */}
                      <button
                        onClick={() => handlePrintAndMarkGuide(null, `GUIA-${order.id}-${Date.now()}`)}
                        className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition duration-200"
                      >
                        Generar Nueva Guía e Imprimir
                      </button>
                    </div>
                  </div>

                  {/* Historial de Estatus */}
                  <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Historial de Estatus</h3>
                    <div className="border-l-2 border-blue-500 pl-4 space-y-2 max-h-48 overflow-y-auto">
                      {orderHistory.length > 0 ? (
                        orderHistory.map((historyItem, index) => (
                          <div key={index} className="text-gray-700">
                            <p className="font-semibold">{historyItem.event_type === 'CAMBIO_ESTATUS' ? `Estado: ${historyItem.new_value}` : historyItem.event_type === 'GUIA_IMPRESA' ? `Guía Impresa: ${historyItem.description.split(': ')[1] || ''}` : historyItem.event_type === 'GUIA_GENERADA_E_IMPRESA' ? `Guía Generada e Impresa: ${historyItem.description.split(': ')[1] || ''}` : historyItem.event_type}</p>
                            <p className="text-sm text-gray-600">{historyItem.description}</p>
                            <p className="text-xs text-gray-500">Por: {historyItem.performed_by_username} el {new Date(historyItem.timestamp).toLocaleString()}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-600">No hay historial de estatus disponible.</p>
                      )}
                    </div>
                  </div>

                </div>
              </>
            )}

            {currentTab === 'facturacion' && (
              <div className="p-4 bg-gray-50 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Opciones de Facturación para este Pedido</h3>
                
                {invoiceMessage && (
                    <div className={`p-2 mb-4 text-center rounded-lg ${invoiceMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {invoiceMessage}
                    </div>
                )}

                {associatedInvoices.length > 0 ? (
                  <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-4" role="alert">
                    <p className="font-bold">Facturas Asociadas:</p>
                    <ul className="list-disc list-inside mt-2">
                      {associatedInvoices.map(inv => (
                        <li key={inv.id} className="flex justify-between items-center">
                          Factura: <strong>{inv.invoice_number}</strong> (Estatus: {inv.status})
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}
                              className="text-red-600 hover:text-red-900"
                              title="Descargar PDF"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd"></path></svg>
                            </button>
                            <button
                              onClick={() => handleDownloadXml(inv.id, inv.invoice_number)}
                              className="text-gray-600 hover:text-gray-900"
                              title="Descargar XML"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd"></path></svg>
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
                    <p className="font-bold">Pedido sin Factura:</p>
                    <p>Este pedido aún no tiene una factura generada.</p>
                    {order.estado_interno === 'Enviada' || order.estado_interno === 'Listo para envío' || order.estado_interno === 'Confirmada' ||  order.estado_interno === 'Preparación' || order.estado_interno === 'Inventario Pendiente' ? ( // Se añadió Inventario Pendiente
                      <form onSubmit={handleGenerateInvoice}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="col-span-2">
                                <h4 className="font-semibold text-gray-800 mb-2">Datos de Facturación del Cliente</h4>
                                <div className="mb-3">
                                    <label htmlFor="clientNameInvoice" className="block text-gray-700 text-sm font-bold mb-1">Nombre Cliente</label>
                                    <input type="text" id="clientNameInvoice" name="clientNameInvoice"
                                        className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={invoiceData.client_name} onChange={(e) => setInvoiceData({...invoiceData, client_name: e.target.value})} required />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="clientRfcInvoice" className="block text-gray-700 text-sm font-bold mb-1">RFC</label>
                                    <input type="text" id="clientRfcInvoice" name="clientRfcInvoice"
                                        className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={invoiceData.client_rfc} onChange={(e) => setInvoiceData({...invoiceData, client_rfc: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="branchInvoice" className="block text-gray-700 text-sm font-bold mb-1">Sucursal</label>
                                    <input type="text" id="branchInvoice" name="branchInvoice"
                                        className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={invoiceData.branch} onChange={(e) => setInvoiceData({...invoiceData, branch: e.target.value})} required />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="invoiceDateInvoice" className="block text-gray-700 text-sm font-bold mb-1">Fecha Factura</label>
                                    <input type="date" id="invoiceDateInvoice" name="invoiceDateInvoice"
                                        className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={invoiceData.invoice_date} onChange={(e) => setInvoiceData({...invoiceData, invoice_date: e.target.value})} required />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="dueDateInvoice" className="block text-gray-700 text-sm font-bold mb-1">Fecha Vencimiento</label>
                                    <input type="date" id="dueDateInvoice" name="dueDateInvoice"
                                        className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        value={invoiceData.due_date} onChange={(e) => setInvoiceData({...invoiceData, due_date: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="invoiceNumberInput" className="block text-gray-700 text-sm font-bold mb-1">Número de Factura (Manual)</label>
                                    <input type="text" id="invoiceNumberInput" name="invoiceNumberInput"
                                    className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    placeholder="Se genera automáticamente si está vacío"
                                    value={invoiceNumberInput} onChange={(e) => setInvoiceNumberInput(e.target.value)} />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="pdfFile" className="block text-gray-700 text-sm font-bold mb-1">Adjuntar PDF</label>
                                    <input type="file" id="pdfFile" name="pdfFile" accept=".pdf"
                                    className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    onChange={handleFileChange} />
                                </div>
                                <div>
                                    <label htmlFor="xmlFile" className="block text-gray-700 text-sm font-bold mb-1">Adjuntar XML</label>
                                    <input type="file" id="xmlFile" name="xmlFile" accept=".xml"
                                    className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    onChange={handleFileChange} />
                                </div>
                            </div>

                            <div className="col-span-2 bg-gray-100 p-4 rounded-lg shadow-inner">
                                <h4 className="font-semibold text-gray-800 mb-2">Desglose de Ítems para Facturar</h4>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">SKU Orig.</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Título</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Cant. Paq.</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">P. Paq.</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Unds/Paq.</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">SKU Unit.</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">P. Unit.</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Total Unds.</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Subtotal Item</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">IVA Item</th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Total Item</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {desgloseItems.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="px-2 py-2 text-xs text-gray-700 min-w-0">{item.original_sku_order}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 min-w-0">{item.item_title_order}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-center min-w-0">{item.quantity_ordered_package}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-right min-w-0">${item.unit_price_package.toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-center min-w-0">{item.unit_quantity_in_package}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 min-w-0">{item.single_unit_sku}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-right min-w-0">${item.single_unit_price.toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-center min-w-0">{item.total_single_units}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-right min-w-0">${item.subtotal_item.toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-right min-w-0">${item.tax_iva_item.toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-xs text-gray-700 text-right min-w-0">${item.total_item.toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="text-right mt-3 pt-3 border-t">
                                    <p className="text-md font-semibold text-gray-800">Subtotal Pedido: ${parseFloat(order.SubTotal || 0).toFixed(2)}</p>
                                    <p className="text-md font-semibold text-gray-800">IVA Pedido: ${parseFloat(order.IVA || 0).toFixed(2)}</p>
                                    <p className="text-xl font-bold text-gray-900">Total Pedido: ${parseFloat(order.Total || 0).toFixed(2)}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-center mt-6">
                            <button
                                type="submit"
                                disabled={invoiceLoading}
                                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {invoiceLoading ? 'Generando Factura...' : 'Generar Factura'}
                            </button>
                        </div>
                      </form>
                    ) : (
                      <p className="mt-2 text-sm">El pedido debe estar como 'Confirmada', 'Inventario Pendiente', 'Listo para envío' o 'Enviada' para generar una factura.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {currentTab === 'paqueteria' && (
                <div className="p-4 bg-gray-50 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Gestión de Guías y Archivos Adjuntos</h3>

                    {newGuideMessage && (
                        <div className={`p-2 mb-4 text-center rounded-lg ${newGuideMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {newGuideMessage}
                        </div>
                    )}

                    {/* Sección para adjuntar nueva guía */}
                    <div className="bg-white p-4 rounded-lg shadow mb-6">
                        <h4 className="font-semibold text-gray-800 mb-2">Adjuntar Nueva Guía / Archivos</h4>
                        <form onSubmit={handleAttachNewGuide} className="space-y-4">
                            <div>
                                <label htmlFor="newGuideNumber" className="block text-gray-700 text-sm font-bold mb-1">Número de Guía (Opcional)</label>
                                <input
                                    type="text"
                                    id="newGuideNumber"
                                    className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    placeholder="Dejar vacío para generar uno automático"
                                    value={newGuideNumberInput}
                                    onChange={(e) => setNewGuideNumberInput(e.target.value)}
                                />
                            </div>
                            <div>
                                <label htmlFor="newGuideCarrier" className="block text-gray-700 text-sm font-bold mb-1">Paquetería</label>
                                <select
                                    id="newGuideCarrier"
                                    className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    value={newGuideCarrier}
                                    onChange={(e) => setNewGuideCarrier(e.target.value)}
                                    required
                                >
                                    {carrierOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="newGuidePdfFile" className="block text-gray-700 text-sm font-bold mb-1">Adjuntar PDF de Guía</label>
                                <input
                                    type="file"
                                    id="newGuidePdfFile"
                                    name="newGuidePdfFile"
                                    accept=".pdf"
                                    className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    onChange={handleNewGuideFileChange}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={newGuideLoading}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline w-full transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {newGuideLoading ? 'Adjuntando...' : 'Adjuntar Guía'}
                            </button>
                        </form>
                    </div>

                    {/* Lista de Guías Existentes */}
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h4 className="font-semibold text-gray-800 mb-2">Guías Asociadas al Pedido</h4>
                        {orderGuides.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Número de Guía</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paquetería</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estatus Impresión</th>
                                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PDF</th>
                                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {orderGuides.map((guide) => (
                                            <tr key={guide.id}>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {order.canal_origen === 'MERCADO LIBRE' ? formatMercadoLibreGuide(guide.guide_number) : guide.guide_number || 'N/A'}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                                    {guide.carrier_name || order.metodo_envio || 'N/A'} {/* Mostrar carrier_name de la guía, si no, el del pedido */}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                                    {guide.print_status === 1 ? (
                                                        <span className="text-green-600 font-medium">Impresa &#10003;</span>
                                                    ) : (
                                                        <span className="text-red-600 font-medium">No Impresa &#10007;</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-center text-sm">
                                                    {guide.pdf_url ? (
                                                        <button onClick={() => handleDownloadAttachedFile(guide.pdf_url, `guia_${guide.guide_number}.pdf`)} className="text-blue-600 hover:text-blue-900 mx-1" title="Descargar PDF">
                                                            <svg className="w-5 h-5 inline-block" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd"></path></svg>
                                                        </button>
                                                    ) : 'N/A'}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-center text-sm font-medium">
                                                    {guide.print_status === 0 && (
                                                        <button
                                                            onClick={() => handlePrintAndMarkGuide(guide.id, guide.guide_number)}
                                                            className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-1 px-2 rounded-lg text-xs transition duration-200"
                                                            title="Imprimir esta guía y marcarla como impresa"
                                                        >
                                                            Imprimir
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-base text-gray-800 font-semibold text-center">No hay guías asociadas.</p>
                        )}
                    </div>
                </div>
            )}
          </div>
        </div>
      );
    }

export default OrderDetailModal;
